import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useVehicles } from '@/hooks/useVehicles'
import { useUrlState } from '@/hooks/useUrlState'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { StatusBadge } from '@/components/ui/status-badge'
import { ExpiryBadge } from '@/components/ui/expiry-badge'
import { Button } from '@/components/ui/button'
import { maskPlate } from '@/components/form/masks'
import type { VehicleListItem } from '@/types/vehicle'
import type { Column } from '@/components/ui/data-table'

const VEHICLE_TYPE_LABEL: Record<string, string> = {
  FIORINO: 'Fiorino',
  VAN: 'Van',
  VUC: 'VUC',
  TOCO: 'Toco',
  TRUCK: 'Truck',
  BITRUCK: 'Bitruck',
  CARRETA: 'Carreta',
  RODOTREM: 'Rodotrem',
}

const COLUMNS: Column<VehicleListItem>[] = [
  {
    key: 'plate',
    header: 'Placa',
    render: (row) => (
      <span className="inline-flex items-center gap-2">
        <span className="font-mono text-sm">{maskPlate(row.plate)}</span>
        <ExpiryBadge date={row.crlvExpiry} label="CRLV" />
        <ExpiryBadge date={row.insuranceExpiry} label="Seguro" />
      </span>
    ),
  },
  {
    key: 'brand',
    header: 'Marca / Modelo',
    render: (row) => {
      const parts = [row.brand, row.model].filter(Boolean).join(' ')
      return parts || '—'
    },
  },
  {
    key: 'year',
    header: 'Ano',
    className: 'hidden lg:table-cell',
    render: (row) => row.year ?? '—',
  },
  {
    key: 'vehicleType',
    header: 'Tipo',
    render: (row) => VEHICLE_TYPE_LABEL[row.vehicleType] ?? row.vehicleType,
  },
  {
    key: 'ownership',
    header: 'Proprietário',
    render: (row) =>
      row.ownership === 'COMPANY'
        ? 'Empresa'
        : row.ownerName
          ? `Motorista: ${row.ownerName}`
          : 'Motorista',
  },
  {
    key: 'active',
    header: 'Status',
    render: (row) => <StatusBadge active={row.active} />,
  },
]

type ActiveFilter = 'all' | 'active' | 'inactive'

const FILTER_LABELS: Record<ActiveFilter, string> = {
  all: 'Todos',
  active: 'Ativos',
  inactive: 'Inativos',
}

export function VehicleListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useUrlState<string>('search', '')
  const [activeFilter, setActiveFilter] = useUrlState<ActiveFilter>('active', 'all')
  const [page, setPage] = useUrlState<number>('page', 1)

  const activeParam =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined

  const { data, isLoading } = useVehicles({ search, active: activeParam, page })

  function handleSearch(v: string) {
    setSearch(v)
    setPage(1)
  }

  function handleFilter(f: ActiveFilter) {
    setActiveFilter(f)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Veículos</h1>
          <p className="text-sm text-muted-foreground">Gerenciamento de veículos</p>
        </div>
        <Button onClick={() => navigate('/vehicles/new')}>
          <Plus size={16} />
          Novo veículo
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por placa, marca ou modelo…"
          className="flex-1"
        />
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(FILTER_LABELS) as ActiveFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => handleFilter(f)}
              className={`px-3 py-1.5 transition-colors ${
                activeFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/vehicles/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhum veículo encontrado"
        emptyDescription="Comece cadastrando o primeiro veículo."
        emptyAction={
          <Button onClick={() => navigate('/vehicles/new')}>
            <Plus size={16} />
            Novo veículo
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  )
}
